import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import time from 'utils/time';
import LogInteractionModal from 'components/Dashboard/Influence/CrmInteractions/LogInteractionModal/LogInteractionModal';
import useCrmInteractionsQuery from 'components/Dashboard/Influence/CrmInteractions/useCrmInteractionsQuery';
import FiltersPopup from 'components/Dashboard/Filters/ui/FiltersPopUp';
import { useFilters } from 'components/Dashboard/Filters/FiltersContext';
import useCompileFiltersFunction from 'components/Dashboard/Filters/useCompileFilters';
import useOnScreen from 'components/Dashboard/hooks/useOnScreen';

const Interactions = (props) => {
  const { StakeholderInformation } = props;
  const { id, contactInformation, firstName, lastName, imageId, imageUrl, email } = StakeholderInformation;
  // Interactions are linked by CrmContact.id, not the generic stakeholder id — for a
  // stakeholder profile page (not a CRM contact edit page) that's contactInformation[0].id.
  // On a CRM contact edit page StakeholderInformation already *is* the CrmContact record.
  const crmContact =
    contactInformation?.[0] ?? (firstName || lastName ? { id, firstName, lastName, imageId, imageUrl, email } : null);
  const crmContactId = crmContact?.id ?? id;
  const preselectedStakeholders = crmContact
    ? [
        {
          value: crmContact.id,
          id: crmContact.id,
          label: `${crmContact.firstName ?? ''} ${crmContact.lastName ?? ''}`.trim(),
          imageId: crmContact.imageId,
          imageUrl: crmContact.imageUrl,
          email: crmContact.email,
        },
      ]
    : [];

  const [showLogModal, setShowLogModal] = useState(false);
  const [showFiltersPopup, setShowFiltersPopup] = useState(false);

  const { getDefaultSectionFilters } = useFilters();
  const { compileFiltersFunction } = useCompileFiltersFunction('CrmInteractions');

  const sectionFilters = getDefaultSectionFilters(null, 'CrmInteractions');
  const { filters: compiledFilters, hash } = compileFiltersFunction(sectionFilters);

  const activeFilterCount = sectionFilters.reduce((count, filter) => {
    if (filter.type === 'search') return filter.value ? count + 1 : count;
    return filter.options?.some((o) => o.selected) ? count + 1 : count;
  }, 0);

  const filters = [
    { field: 'stakeholderContactIds', operator: 'str_eq', value: String(crmContactId) },
    ...(compiledFilters ?? []),
  ];

  const { interactions, isLoading, isLoadingScroll, thereIsMoreContent, handleScrollBottom, addInteraction } =
    useCrmInteractionsQuery({
      filters,
      sort: { field: 'createdAtUtc', order: 'DESC' },
      dependencyKey: `${crmContactId}-${hash}`,
      enabled: !!crmContactId,
    });

  const [heightContainer, containerRef] = useHeightContainer();

  const sentinelRef = useRef(null);
  const isSentinelVisible = useOnScreen(sentinelRef, interactions?.length, '200px 0px 200px 0px');

  useEffect(() => {
    if (isSentinelVisible && thereIsMoreContent && !isLoading && !isLoadingScroll) {
      handleScrollBottom();
    }
  }, [isSentinelVisible, thereIsMoreContent, isLoading, isLoadingScroll]);

  return (
    <div className='border-bottom'>
      <div
        className='px-4 specific-bottom-padding-31 specific-top-padding-7 d-flex align-items-center justify-content-between'
        style={{ height: `75.6px` }}
      >
        <button
          className='text-left action-state-hover general-button py-1 pr-3 specific-left-padding-6 text-light-blue border-general border-light-blue rounded-pill d-flex align-items-center'
          onClick={() => setShowLogModal(true)}
        >
          <span className='icon-plus-rounded mr-2 title-h1' />
          <span className='mb-0'>Log an interaction</span>
        </button>
        <button
          className={`sort-button more-filters-button ml-2 flex-shrink-0 ${activeFilterCount > 0 ? 'more-filters-button-active' : ''}`}
          style={{ height: '38px' }}
          onClick={() => setShowFiltersPopup(true)}
        >
          <span className='icon-filters' />
          <span className='ml-1'>Filters</span>
          {activeFilterCount > 0 && <span className='ml-1'>({activeFilterCount})</span>}
        </button>
      </div>

      <div className='border-top pt-2 pb-4' ref={containerRef} style={{ minHeight: `${heightContainer - 1}px` }}>
        {isLoading ? (
          <LoadingStateInteractions />
        ) : interactions?.length > 0 ? (
          <>
            {interactions.map((item) => (
              <InteractionItem key={`interaction-${item.id}`} item={item} />
            ))}
            <div ref={sentinelRef} />
            {isLoadingScroll && <LoadingStateInteractions count={1} />}
          </>
        ) : (
          <div
            className='flex-centered justify-content-center text-center'
            style={{ height: `${heightContainer - 36}px` }}
          >
            <p className='main-light-text title-h5-m' style={{ maxWidth: '420px' }}>
              {activeFilterCount > 0
                ? 'No interactions match the current filters'
                : 'No interactions logged with this stakeholder yet'}
            </p>
          </div>
        )}
      </div>

      {showLogModal && (
        <LogInteractionModal
          onClose={() => setShowLogModal(false)}
          preselectedStakeholders={preselectedStakeholders}
          onSuccess={(newInteraction) => {
            addInteraction(newInteraction);
            setShowLogModal(false);
          }}
        />
      )}

      <FiltersPopup
        section='CrmInteractions'
        openState={{ isOpen: showFiltersPopup, setIsOpen: setShowFiltersPopup }}
      />
    </div>
  );
};

const InteractionItem = ({ item }) => {
  const { title, interactionType, dateTime, addedByUserFullName, description } = item;
  const [isExpanded, setIsExpanded] = useState(false);
  const descriptionRef = useRef(null);
  const [isExpandable, setIsExpandable] = useState(false);
  const [fullHeight, setFullHeight] = useState(0);

  useLayoutEffect(() => {
    if (descriptionRef.current) {
      // clientHeight is capped by the collapsed max-height (~1 line); scrollHeight is the
      // paragraph's real, unclipped height. If the real content needs more room, it's expandable.
      // A huge placeholder max-height (e.g. 1000px) would make the transition interpolate over
      // a range far bigger than the real content, so the visible growth finishes almost
      // instantly — animate to the actual measured height instead.
      setFullHeight(descriptionRef.current.scrollHeight + 2);
      setIsExpandable(descriptionRef.current.scrollHeight > descriptionRef.current.clientHeight + 1);
    }
  }, [description]);

  return (
    <div
      className={`position-relative stakeholder-interaction-item border-bottom pb-3 ${isExpandable ? 'pointer' : ''}`}
      onClick={isExpandable ? () => setIsExpanded((prev) => !prev) : undefined}
    >
      <div className='px-4 pt-3'>
        <p className='mb-1 paragraph-p2 main-light-text'>
          <span className='text-main-blue font-weight-bold'>{interactionType}</span>
          <span className='dot d-inline-block-centered'></span>
          {addedByUserFullName}
          <span className='dot d-inline-block-centered'></span>
          {/T00:00:00$/.test(dateTime) ? time(dateTime).format('DD MMM[,] YYYY') : time(dateTime).formatEmbargoedDate()}
        </p>
        <p className='mb-1 font-weight-bold main-dark-text'>{title}</p>
        {description ? (
          <p
            ref={descriptionRef}
            className='mb-0 paragraph-p2 main-light-text pre-wrap interaction-description'
            style={{ lineHeight: 1.35, maxHeight: isExpanded ? fullHeight : undefined }}
          >
            {description}
          </p>
        ) : (
          <p className='mb-0 paragraph-p2 main-light-text'>No description added</p>
        )}
        {isExpandable && <span className={`dropdown-item-element ${isExpanded ? 'open' : ''}`} />}
      </div>
    </div>
  );
};

const LoadingStateInteractions = ({ count = 2 }) => (
  <>
    {Array(count)
      .fill()
      .map((_, index) => (
        <SkeletonTheme baseColor='#fff' key={index}>
          <div className='px-4 pt-3 pb-2'>
            <div className='d-flex align-items-start mb-2'>
              <Skeleton height={22} width={80} className='rounded-pill mr-2' />
              <div className='pl-1'>
                <Skeleton height={16} width={280} />
                <div className='mt-1'>
                  <Skeleton height={14} width={160} />
                </div>
              </div>
            </div>
          </div>
        </SkeletonTheme>
      ))}
  </>
);

export default Interactions;
